import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Drink, Brand, Flavor, Attribute, DrinkFlavor, DrinkAttribute } from '@/types/firestore';
import { Loader2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectAllBrands,
  selectAllFlavors,
  selectAllAttributes,
  selectGlobalDataLoading,
  selectGlobalDataError,
  selectGlobalDataInitialized,
  selectDrinkById,
  fetchDrinkById,
  selectFetchDrinkByIdStatus,
  selectFetchDrinkByIdError
} from "@/store/slices/globalDataSlice";
import {
  updateDrink,
  selectEditorOperationLoading,
  selectEditorOperationError,
  selectSelectedEntity
} from "@/store/slices/editorSlice";
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';

interface DrinkEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DrinkEditDialog: React.FC<DrinkEditDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const dispatch = useAppDispatch();
  const selectedEntity = useAppSelector(selectSelectedEntity);

  const brands = useAppSelector(selectAllBrands);
  const flavors = useAppSelector(selectAllFlavors);
  const attributes = useAppSelector(selectAllAttributes);
  
  const globalDataLoadingStatus = useAppSelector(selectGlobalDataLoading);
  const globalDataError = useAppSelector(selectGlobalDataError);
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);

  const drinkFromStore = useAppSelector(state =>
    selectedEntity?.type === 'drink' && selectedEntity.id
      ? selectDrinkById(state, selectedEntity.id)
      : null
  );
  const fetchSpecificDrinkStatus = useAppSelector(selectFetchDrinkByIdStatus);
  const fetchSpecificDrinkError = useAppSelector(selectFetchDrinkByIdError);

  const saveOperationLoading = useAppSelector(selectEditorOperationLoading) === 'pending';
  const saveOperationError = useAppSelector(selectEditorOperationError);

  const [name, setName] = useState('');
  const [brandId, setBrandId] = useState('');
  const [selectedBrandName, setSelectedBrandName] = useState('');
  const [selectedFlavors, setSelectedFlavors] = useState<DrinkFlavor[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<DrinkAttribute[]>([]);
  
  const [currentEditingDrink, setCurrentEditingDrink] = useState<Drink | null>(null);

  useEffect(() => {
    const entityId = selectedEntity?.id;
    if (open && selectedEntity?.type === 'drink' && entityId) {
      if (drinkFromStore) {
        setCurrentEditingDrink(drinkFromStore);
      } else if (globalDataInitialized && fetchSpecificDrinkStatus !== 'pending') {
        dispatch(fetchDrinkById(entityId));
      }
    } else if (!open) {
      setCurrentEditingDrink(null);
      setName('');
      setBrandId('');
      setSelectedBrandName('');
      setSelectedFlavors([]);
      setSelectedAttributes([]);
    }
  }, [
    open, 
    selectedEntity, 
    drinkFromStore, 
    globalDataInitialized, 
    fetchSpecificDrinkStatus, 
    dispatch
  ]);

  useEffect(() => {
    if (currentEditingDrink) {
      setName(currentEditingDrink.name || '');
      setBrandId(currentEditingDrink.brandId || '');
      setSelectedBrandName(currentEditingDrink.brandName || '');
      setSelectedFlavors(currentEditingDrink.flavors || []);
      setSelectedAttributes(currentEditingDrink.attributes || []);
    }
  }, [currentEditingDrink]);

  useEffect(() => {
    if (brandId) {
      const selected = brands.find((b: Brand) => b.id === brandId);
      if (selected) {
        setSelectedBrandName(selected.name);
      }
    } else {
      setSelectedBrandName('');
    }
  }, [brandId, brands]);
  
  const handleSubmit = async () => {
    if (!currentEditingDrink || !currentEditingDrink.id || saveOperationLoading || derivedIsLoading) return;

    const resultAction = await dispatch(updateDrink({
      id: currentEditingDrink.id,
      data: {
        name,
        brandId,
        brandName: selectedBrandName,
        brandSize: brands.find(b => b.id === brandId)?.brandSize || 'unknown',
        flavors: selectedFlavors,
        attributes: selectedAttributes,
      }
    }));

    if (updateDrink.fulfilled.match(resultAction)) {
      handleOpenChangeInternal(false);
    }
  };

  const toggleFlavor = (flavor: Flavor) => {
    setSelectedFlavors((prevFlavors) => {
      const existingIndex = prevFlavors.findIndex((f) => f.id === flavor.id);
      if (existingIndex >= 0) {
        return prevFlavors.filter((_, i) => i !== existingIndex);
      } else {
        return [...prevFlavors, { id: flavor.id || '', name: flavor.name, groupId: flavor.groupId }];
      }
    });
  };

  const toggleAttribute = (attribute: Attribute) => {
    setSelectedAttributes((prevAttributes) => {
      const existingIndex = prevAttributes.findIndex((a) => a.id === attribute.id);
      if (existingIndex >= 0) {
        return prevAttributes.filter((_, i) => i !== existingIndex);
      } else {
        return [...prevAttributes, { id: attribute.id || '', name: attribute.name, groupId: attribute.groupId }];
      }
    });
  };

  const groupedFlavors = useMemo(() => {
    const groups: { [key: string]: Flavor[] } = {};
    flavors.forEach((flavor: Flavor) => {
      const groupName = flavor.groupId?.toString() || 'Uncategorized';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(flavor);
    });
    return groups;
  }, [flavors]);

  const groupedAttributes = useMemo(() => {
    const groups: { [key: string]: Attribute[] } = {};
    attributes.forEach((attribute: Attribute) => {
      const groupName = attribute.groupId?.toString() || 'Uncategorized';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(attribute);
    });
    return groups;
  }, [attributes]);

  const isFlavorSelected = (flavorId: string) => selectedFlavors.some((f) => f.id === flavorId);
  const isAttributeSelected = (attributeId: string) => selectedAttributes.some((a) => a.id === attributeId);

  const handleOpenChangeInternal = (newOpenState: boolean) => {
    onOpenChange(newOpenState);
  };

  const handleCancel = () => {
    handleOpenChangeInternal(false);
  };
  
  const derivedIsLoading = globalDataLoadingStatus === 'pending' || fetchSpecificDrinkStatus === 'pending';
  const derivedError = globalDataError || fetchSpecificDrinkError;

  const uiDisabled = derivedIsLoading || saveOperationLoading;

  if (!open) return null;

  if (derivedIsLoading && !currentEditingDrink) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] flex items-center justify-center">
          <DialogHeader>
            <DialogTitle>Loading Data</DialogTitle>
            <DialogDescription>
              Please wait while the drink details are being loaded.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="mr-2 h-8 w-8 animate-spin" />
            <span>Loading drink details...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (derivedError && !currentEditingDrink) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>{derivedError}</DialogDescription>
          </DialogHeader>
          <DialogFooter><Button variant="outline" onClick={handleCancel}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!currentEditingDrink && globalDataInitialized && fetchSpecificDrinkStatus !== 'pending' && !derivedError) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh]">
           <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>Could not load drink data. Please ensure the drink exists or try again.</DialogDescription>
            </DialogHeader>
           <DialogFooter><Button variant="outline" onClick={handleCancel}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (!currentEditingDrink) {
      return null; 
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChangeInternal}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Edit Drink: {currentEditingDrink?.name || 'Loading...'}</DialogTitle>
          <DialogDescription>
            Update the drink properties below.
            {derivedIsLoading && <span className="text-xs text-muted-foreground ml-2">(Updating...)</span>}
          </DialogDescription>
        </DialogHeader>

        {globalDataLoadingStatus === 'failed' && globalDataError && (
            <p className="text-red-500 p-4">Error loading brands/flavors/attributes: {globalDataError}</p>
        )}

        <ScrollArea className={`flex-1 pr-4 ${uiDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-6 items-center gap-4">
              <Label htmlFor="name" className="text-right col-span-1">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="col-span-5" disabled={uiDisabled} />
            </div>

            <div className="grid grid-cols-6 items-center gap-4">
              <Label htmlFor="brand" className="text-right col-span-1">Brand</Label>
              <div className="col-span-5">
                <Select value={brandId} onValueChange={setBrandId} disabled={uiDisabled}>
                  <SelectTrigger>
                    <SelectValue placeholder={globalDataLoadingStatus === 'pending' ? "Loading brands..." : "Select a brand"} />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((b: Brand) => (
                      <SelectItem key={b.id} value={b.id || ''}>
                        {b.name} - {b.brandSize === 'craft' ? 'Craft' : b.brandSize === 'mass' ? 'Commercial' : 'Unknown'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-3 block">Flavors</Label>
              <div className="">
                {Object.entries(groupedFlavors).map(([groupKey, flavorsInGroup], groupIndex) => {
                  return (
                    <div
                      key={groupKey}
                      className={`space-y-2 p-2 rounded-md ${groupIndex % 2 === 1 ? 'bg-muted/50' : ''}`}
                    >
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2 pl-1">
                        {flavorsInGroup.map((flavor) => (
                          <div key={flavor.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`flavor-checkbox-${flavor.id}`}
                              checked={isFlavorSelected(flavor.id || '')}
                              onCheckedChange={() => toggleFlavor(flavor)}
                              aria-labelledby={`flavor-label-${flavor.id}`}
                              disabled={uiDisabled}
                            />
                            <label
                              htmlFor={`flavor-checkbox-${flavor.id}`}
                              id={`flavor-label-${flavor.id}`}
                              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none"
                            >
                              {flavor.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {globalDataLoadingStatus !== 'pending' && flavors.length === 0 && (
                    <p className="text-muted-foreground text-sm pl-1">No flavors found.</p>
                 )}
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-3 block">Attributes</Label>
              <div className="">
                {Object.entries(groupedAttributes).map(([groupKey, attributesInGroup], groupIndex) => {
                  return (
                    <div
                      key={groupKey}
                      className={`space-y-2 p-2 rounded-md ${groupIndex % 2 === 1 ? 'bg-muted/50' : ''}`}
                    >
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2 pl-1">
                        {attributesInGroup.map((attribute) => (
                          <div key={attribute.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`attribute-checkbox-${attribute.id}`}
                              checked={isAttributeSelected(attribute.id || '')}
                              onCheckedChange={() => toggleAttribute(attribute)}
                              aria-labelledby={`attribute-label-${attribute.id}`}
                              disabled={uiDisabled}
                            />
                            <label
                              htmlFor={`attribute-checkbox-${attribute.id}`}
                              id={`attribute-label-${attribute.id}`}
                              className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none"
                            >
                              {attribute.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {globalDataLoadingStatus !== 'pending' && attributes.length === 0 && (
                    <p className="text-muted-foreground text-sm pl-1">No attributes found.</p>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          {saveOperationError && (
            <p className="text-red-500 text-sm mr-auto">Error saving: {saveOperationError}</p>
          )}
          <Button type="button" variant="outline" onClick={handleCancel} disabled={uiDisabled}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={uiDisabled}>
            {saveOperationLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 